import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SettingsService } from '@app/services/settings/settings.service';
import { ShopService } from '@app/services/shops/shop.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { incotermFields } from './model';
import { takeUntil } from 'rxjs/operators';
import { InternationalShipmentSettings } from '@app/services/settings/settings-dto';
import { InternationalSettings } from '@pdqdev/pdq-shared/dist/entities';
import { CustomDeclarationCalculatedBy, InternationalShipmentContentType } from '@pdqdev/pdq-shared/dist/interfaces';

@Component({
  selector: 'international',
  templateUrl: './international.component.html',
  styleUrls: ['./international.component.scss', '../settings.scss'],
  standalone: false,
})
export class InternationalComponent implements OnInit {
  isLoading = true;
  isActiveInternational = false;
  fields: any;
  formGroup: UntypedFormGroup;
  initialForm: UntypedFormGroup;
  notification = { duration: 'short', msg: '', type: 'info', state: 'out' };
  editComplete$: Observable<any>;
  editCompleteSubscription$: Subscription;
  shop$: Subject<any> = new Subject<any>();
  shopId: number;
  internationalSettings$ = new Subject<any>();
  internationalSettingsDynamicFields$ = new Subject<any>();
  internationalSettingsDTO: InternationalShipmentSettings;
  internationalSettingsShopDynamicFields;

  constructor(
    private shopService: ShopService,
    private formBuilder: UntypedFormBuilder,
    private cdRef: ChangeDetectorRef,
    private settingsSrv: SettingsService,
  ) {}

  ngOnInit(): void {
    this.initMetadata();
  }

  initMetadata() {
    this.shopService.shop$.pipe(takeUntil(this.shop$)).subscribe((shop) => {
      if (!shop || !shop.metadata) return;
      this.shop$.next(shop);
      this.shopId = shop.id;
      this.settingsSrv
        .getIntlDynamicFields()
        .pipe(takeUntil(this.internationalSettingsDynamicFields$))
        .subscribe((settingsDynamicFields) => {
          if (settingsDynamicFields && !this.internationalSettingsShopDynamicFields) {
            this.internationalSettingsShopDynamicFields = settingsDynamicFields;
          }
        });

      if (shop.internationalSettings?.id) {
        this.settingsSrv
          .getIntlSettings()
          .pipe(takeUntil(this.internationalSettings$))
          .subscribe((settings) => {
            if (settings) {
              this.internationalSettingsDTO = new InternationalShipmentSettings(settings);
              this.isActiveInternational = !!settings?.isActive;
              this.internationalSettings$.next(this.internationalSettingsDTO);
              this.initForm(this.internationalSettingsDTO);
              this.isLoading = false;
            }
          });
      } else {
        this.internationalSettingsDTO = new InternationalShipmentSettings();
        this.isActiveInternational = false;
        this.initForm(null);
        this.isLoading = false;
      }
    });
  }

  initForm(internationalSettings: InternationalShipmentSettings) {
    // const intlMetadata = shopMetadata?.internationalSettings;
    // const taxControls = taxFields.reduce((res, field) => {
    //   res[field.name] = [(intlMetadata && intlMetadata[field.name]) || '', field.validator ?? undefined];
    //   return res;
    // }, {});

    const taxControls = {};
    taxControls['EEI'] = [internationalSettings?.eeiTaxId?.taxId ?? '', undefined];
    taxControls['IOSS'] = [internationalSettings?.iossTaxId?.taxId ?? '', [Validators.pattern('[a-zA-Z0-9_]{12}')]];
    taxControls['EORI'] = [internationalSettings?.eoriTaxId?.taxId ?? '', [Validators.pattern('GB([0-9]){12}')]];
    taxControls['isTaxIDsAutoApplyActive'] = [internationalSettings?.isTaxIDsAutoApplyActive ?? '', undefined];
    taxControls['VAT_IDS'] = [internationalSettings?.vatIds ?? '', undefined];
    taxControls['customDeclarationCalculatedBy'] = [
      internationalSettings?.customDeclarationCalculatedBy
        ? internationalSettings?.customDeclarationCalculatedBy
        : this.isActiveInternational
          ? CustomDeclarationCalculatedBy.ITEM_PRICE
          : CustomDeclarationCalculatedBy.ITEM_DISCOUNTED_PRICE,
      undefined,
    ];
    const contentsType = internationalSettings?.contentsType ?? InternationalShipmentContentType.MERCHANDISE;
    const incotermControlsArr = Object.values(incotermFields);
    const incotermControls = incotermControlsArr.reduce((res, field) => {
      res[field.name] = [(internationalSettings && internationalSettings[field.name]) || field.defaultValue || ''];
      return res;
    }, {});

    const formFields = {
      isActiveInternational: [!!internationalSettings?.isActiveInternational],
      ...taxControls,
      ...incotermControls,
      contentsType,
    };

    this.fields = formFields;
    this.formGroup = this.formBuilder.group(formFields);
    this.initialForm = this.formGroup.value;
    this.cdRef.detectChanges();
  }

  onSubmit(): void {
    if (!this.formGroup.valid || !this.formGroup.dirty) return;
    const values = this.formGroup.value;

    const intlSettings: InternationalSettings = this.getInternationalSettingsFromFormValues(values);
    this.editComplete$ = this.settingsSrv.saveIntlSettings(intlSettings);
    this.editCompleteSubscription$ = this.editComplete$.subscribe(
      (res) => {
        this.notify('info', 'settings saved successfully');
        this.setInitialValues(res);
        this.shopService.refreshShop();
      },
      (err) => {
        this.notify('fail', 'Failed to save settings');
      },
    );
  }

  getInternationalSettingsFromFormValues(values) {
    this.internationalSettingsDTO.category = values.category;
    this.internationalSettingsDTO.certifiedSigner = values.certifiedSigner;
    this.internationalSettingsDTO.incoterm = values.incoterm;
    this.internationalSettingsDTO.nonDeliveryOption = values.nonDeliveryOption;
    this.internationalSettingsDTO.isTaxIDsAutoApplyActive = values.isTaxIDsAutoApplyActive;
    this.internationalSettingsDTO.isActiveInternational = values.isActiveInternational;
    this.internationalSettingsDTO.customDeclarationCalculatedBy = values.customDeclarationCalculatedBy;
    this.internationalSettingsDTO.contentsType = values?.contentsType;
    this.internationalSettingsDTO.contentsTypeDescription =
      this.internationalSettingsDTO.contentsType === InternationalShipmentContentType.OTHER
        ? values?.contentsTypeDescription
        : '';
    let eeiTaxIdentifier: any = undefined;
    if (values['EEI'] && values['EEI'] !== '') {
      eeiTaxIdentifier = this.internationalSettingsDTO.eeiTaxId ?? {};
      eeiTaxIdentifier.taxId = values['EEI'];
    }
    this.internationalSettingsDTO.eeiTaxId = eeiTaxIdentifier;

    let eoriTaxIdentifier: any = undefined;
    if (values['EORI'] && values['EORI'] !== '') {
      eoriTaxIdentifier = this.internationalSettingsDTO.eoriTaxId ?? {};
      eoriTaxIdentifier.taxId = values['EORI'];
    }
    this.internationalSettingsDTO.eoriTaxId = eoriTaxIdentifier;

    let iossTaxIdentifier: any = undefined;
    if (values['IOSS'] && values['IOSS'] !== '') {
      iossTaxIdentifier = this.internationalSettingsDTO.iossTaxId ?? {};
      iossTaxIdentifier.taxId = values['IOSS'];
    }
    this.internationalSettingsDTO.iossTaxId = iossTaxIdentifier;

    let vatTaxIds: any = [];
    if (values['VAT_IDS'] && values['VAT_IDS'].length > 0) {
      vatTaxIds = values['VAT_IDS'];
    }
    this.internationalSettingsDTO.vatIds = vatTaxIds;

    return this.internationalSettingsDTO.getInternationalShipmentBackendDTO(this.shopId);
  }

  setInitialValues(values: InternationalSettings) {
    this.internationalSettingsDTO = new InternationalShipmentSettings(values);
    this.isActiveInternational = !!values?.isActive;
    this.initForm(this.internationalSettingsDTO);

    // this.initialForm = values;
    this.formGroup.markAsPristine();
  }

  ///// UI Getters ////////////////
  get isDirty() {
    if (!this.formGroup?.controls) return;
    return Object.entries(this.formGroup?.controls).find((c) => c[0] !== 'supportInternational' && c[1].dirty)
      ? true
      : false;
  }
  get changesCount() {
    if (!this.formGroup || !this.initialForm) return;
    return Object.entries(this.formGroup?.controls).filter((value) => {
      if (value[1] instanceof UntypedFormControl) return value[1].value != this.initialForm[value[0]];
      if (value[1] instanceof UntypedFormGroup)
        return (
          value[1].value[value[0]] != this.initialForm[value[0]][value[0]] &&
          value[1].value[value[0]] != this.initialForm[value[0]]
        );
    }).length;
  }
  get changedStatusTxt() {
    if (!this.isChanged) return '';
    return ` You've changed ${this.changesCount} setting `;
  }
  get isChanged() {
    return this.formGroup.dirty && this.changesCount > 0;
  }
  get isValid() {
    return this.formGroup.valid;
  }
  get footerStateClasses() {
    return !this.formGroup.dirty ? ['initial'] : this.isChanged ? ['apear'] : ['disapear'];
  }
  get fieldsAreaClasses() {
    return this.footerStateClasses.includes('apear') ? ['footerBottomPadding'] : [''];
  }

  //// private methods

  resetForm() {
    this.formGroup.setValue({ ...this.initialForm });
  }

  getObjValueByPath(obj: object, path: string) {
    if (path.includes(',')) {
      const pathes = path.split(',');
      return pathes.every((path) => {
        const val = path.split('.').reduce((val, currPath) => (val ? val[currPath] : ''), obj);
        return val;
      });
    }
    const val = path.split('.').reduce((val, currPath) => (val ? val[currPath] : ''), obj);

    return val;
  }

  notify(type: string, msg: string) {
    this.notification.duration = 'short';
    this.notification.msg = msg;
    this.notification.type = type;
    this.notification.state = 'in';

    setTimeout(() => (this.notification.state = 'out'), 5000);
  }
}
